import React, { useEffect, useState, useCallback } from "react";
import * as func from "../../api/apiHelper";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBrain,
  faPlus,
  faPencil,
  faTrash,
  faSpinner,
  faTimes,
  faUsers,
  faChartBar,
  faListOl,
  faFire,
} from "@fortawesome/free-solid-svg-icons";

const CATEGORIES = ["life", "lifestyle", "values", "finances", "health", "context"];
const DAY_LABELS = { 0: "Day 0 (Signup)", 1: "Day 1", 2: "Day 2", 3: "Day 3" };

const CATEGORY_COLORS = {
  life: "bg-blue-100 text-blue-700 border-blue-200",
  lifestyle: "bg-green-100 text-green-700 border-green-200",
  values: "bg-purple-100 text-purple-700 border-purple-200",
  finances: "bg-yellow-100 text-yellow-700 border-yellow-200",
  health: "bg-red-100 text-red-700 border-red-200",
  context: "bg-gray-100 text-gray-600 border-gray-200",
};

const BAR_COLORS = [
  "bg-indigo-500", "bg-violet-500", "bg-sky-500", "bg-emerald-500",
  "bg-amber-500", "bg-rose-500", "bg-teal-500", "bg-fuchsia-500",
];

const emptyForm = () => ({
  questionTextBride: "",
  questionTextGroom: "",
  category: "life",
  weight: 1,
  isScoring: true,
  dayToShow: 0,
  isHighImpact: false,
  options: [
    { value: "opt_a", textBride: "", textGroom: "", displayOrder: 1 },
    { value: "opt_b", textBride: "", textGroom: "", displayOrder: 2 },
  ],
});

const QuestionModal = ({ initial, onSave, onClose }) => {
  const [form, setForm] = useState(initial ?? emptyForm());
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setOption = (i, k, v) =>
    setForm(f => ({ ...f, options: f.options.map((o, idx) => idx === i ? { ...o, [k]: v } : o) }));
  const addOption = () =>
    setForm(f => ({ ...f, options: [...f.options, { value: `opt_${f.options.length + 1}`, textBride: "", textGroom: "", displayOrder: f.options.length + 1 }] }));
  const removeOption = (i) =>
    setForm(f => ({ ...f, options: f.options.filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    if (!form.questionTextBride || !form.questionTextGroom) {
      toast.warn("Both bride and groom question texts are required.");
      return;
    }
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-5 border-b sticky top-0 bg-white z-10">
          <h2 className="font-semibold text-lg text-gray-800">{initial ? "Edit Question" : "New Question"}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Question (Bride)</label>
              <textarea rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={form.questionTextBride} onChange={(e) => set("questionTextBride", e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Question (Groom)</label>
              <textarea rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={form.questionTextGroom} onChange={(e) => set("questionTextGroom", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
              <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={form.category} onChange={(e) => set("category", e.target.value)}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Day to Show</label>
              <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={form.dayToShow} onChange={(e) => set("dayToShow", Number(e.target.value))}>
                {Object.entries(DAY_LABELS).map(([d, label]) => (
                  <option key={d} value={d}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Weight</label>
              <input type="number" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={form.weight} onChange={(e) => set("weight", Number(e.target.value))} />
            </div>
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.isScoring} onChange={(e) => set("isScoring", e.target.checked)} />
              Scoring
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.isHighImpact} onChange={(e) => set("isHighImpact", e.target.checked)} />
              High Impact
            </label>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-gray-500">Options</p>
              <button onClick={addOption} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                <FontAwesomeIcon icon={faPlus} /> Add Option
              </button>
            </div>
            <div className="space-y-2">
              {form.options.map((opt, i) => (
                <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                  <input className="w-20 border border-gray-200 rounded px-2 py-1 text-xs" placeholder="value"
                    value={opt.value} onChange={(e) => setOption(i, "value", e.target.value)} />
                  <input className="flex-1 border border-gray-200 rounded px-2 py-1 text-xs" placeholder="Text (bride)"
                    value={opt.textBride} onChange={(e) => setOption(i, "textBride", e.target.value)} />
                  <input className="flex-1 border border-gray-200 rounded px-2 py-1 text-xs" placeholder="Text (groom)"
                    value={opt.textGroom} onChange={(e) => setOption(i, "textGroom", e.target.value)} />
                  <button onClick={() => removeOption(i)} className="text-red-400 hover:text-red-600">
                    <FontAwesomeIcon icon={faTimes} className="text-xs" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 p-5 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2">
            {saving && <FontAwesomeIcon icon={faSpinner} spin />}
            Save Question
          </button>
        </div>
      </div>
    </div>
  );
};

// Answer distribution bar for a single option
const AnswerBar = ({ label, count, pct, colorClass }) => (
  <div className="flex items-center gap-3 py-1">
    <span className="text-xs text-gray-500 w-32 truncate shrink-0">{label}</span>
    <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
      <div className={`h-2 rounded-full transition-all duration-500 ${colorClass}`} style={{ width: `${pct}%` }} />
    </div>
    <span className="text-xs font-semibold text-gray-700 w-12 text-right shrink-0">{count.toLocaleString()}</span>
    <span className="text-xs text-gray-400 w-9 text-right shrink-0">{pct}%</span>
  </div>
);

// Full question card with analytics
const QuestionCard = ({ q, onEdit, onDelete, deleting, showAnalytics }) => {
  const answeredCount = q.stats?.totalAnswers ?? 0;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="flex items-start gap-4 px-5 py-4">
        {/* Question number badge */}
        <div className="shrink-0 w-9 h-9 rounded-xl bg-indigo-600 text-white flex items-center justify-center font-bold text-sm">
          Q{q.globalNumber}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${CATEGORY_COLORS[q.category] || CATEGORY_COLORS.context}`}>
              {q.category}
            </span>
            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
              {DAY_LABELS[q.dayToShow] ?? `Day ${q.dayToShow}`}
            </span>
            {q.isHighImpact && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 flex items-center gap-1">
                <FontAwesomeIcon icon={faFire} className="text-[10px]" /> High Impact
              </span>
            )}
            {!q.isScoring && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Non-scoring</span>
            )}
            <span className="text-xs text-gray-400">wt: {q.weight}</span>
          </div>

          <p className="text-sm font-semibold text-gray-800">{q.questionTextBride}</p>
          <p className="text-xs text-gray-500 mt-0.5 italic">{q.questionTextGroom}</p>

          {/* Analytics section */}
          {showAnalytics && (
            <div className="mt-3 pt-3 border-t border-gray-50">
              <div className="flex items-center gap-2 mb-2">
                <FontAwesomeIcon icon={faChartBar} className="text-gray-400 text-xs" />
                <span className="text-xs font-medium text-gray-500">
                  {answeredCount.toLocaleString()} response{answeredCount !== 1 ? "s" : ""}
                </span>
              </div>
              {answeredCount === 0 ? (
                <p className="text-xs text-gray-400 italic">No responses yet.</p>
              ) : (
                <div className="space-y-0.5">
                  {q.stats.options.map((opt, idx) => {
                    // Find label from question options array
                    const optDef = q.options?.find(o => o.value === opt.answerValue);
                    const label = optDef?.textBride || opt.answerValue;
                    return (
                      <AnswerBar
                        key={opt.answerValue}
                        label={label}
                        count={opt.count}
                        pct={opt.pct}
                        colorClass={BAR_COLORS[idx % BAR_COLORS.length]}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Options chips (when analytics hidden) */}
          {!showAnalytics && q.options?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {q.options.map((o, i) => (
                <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                  {o.textBride || o.value}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-3 shrink-0 pt-0.5">
          <button onClick={onEdit} className="text-blue-500 hover:text-blue-700" title="Edit">
            <FontAwesomeIcon icon={faPencil} />
          </button>
          <button onClick={onDelete} disabled={deleting}
            className="text-red-400 hover:text-red-600 disabled:opacity-50" title="Delete">
            <FontAwesomeIcon icon={deleting ? faSpinner : faTrash} spin={deleting} />
          </button>
        </div>
      </div>
    </div>
  );
};

const AlignmentQuestions = () => {
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [totalRespondents, setTotalRespondents] = useState(0);
  const [modal, setModal] = useState(null);
  const [filterDay, setFilterDay] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(true);
  const [activeTab, setActiveTab] = useState("questions"); // "questions" | "analytics"

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await func.get("mst-admin/alignment-questions/analytics");
      if (res?.status === 200 && res.data?.success) {
        setQuestions(res.data.questions || []);
        setTotalRespondents(res.data.totalRespondents ?? 0);
      } else {
        toast.error("Failed to load questions");
      }
    } catch {
      toast.error("Failed to load questions");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (form) => {
    try {
      let res;
      if (modal.mode === "edit") {
        res = await func.put(`mst-admin/alignment-questions/${modal.data._id}`, form);
      } else {
        res = await func.post("mst-admin/alignment-questions", form);
      }
      if (res?.status === 200 || res?.status === 201) {
        toast.success(modal.mode === "edit" ? "Question updated" : "Question created");
        setModal(null);
        fetchData();
      } else {
        toast.error(res?.data?.message || "Save failed");
      }
    } catch {
      toast.error("Save failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this question? This cannot be undone.")) return;
    setDeleting(id);
    try {
      const res = await func.del(`mst-admin/alignment-questions/${id}`);
      if (res?.status === 200) {
        toast.success("Question deleted");
        fetchData();
      } else {
        toast.error("Delete failed");
      }
    } catch {
      toast.error("Delete failed");
    }
    setDeleting(null);
  };

  const filtered = questions.filter(q => {
    if (filterDay !== "" && String(q.dayToShow) !== filterDay) return false;
    if (filterCategory && q.category !== filterCategory) return false;
    return true;
  });

  const byDay = [0, 1, 2, 3].reduce((acc, d) => {
    acc[d] = filtered.filter(q => q.dayToShow === d);
    return acc;
  }, {});

  const totalAnswered = questions.filter(q => (q.stats?.totalAnswers ?? 0) > 0).length;
  const completionRate = questions.length > 0 ? Math.round((totalAnswered / questions.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-700 to-violet-700 px-6 py-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                <FontAwesomeIcon icon={faBrain} className="text-white text-lg" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Match DNA Questions</h1>
                <p className="text-indigo-200 text-xs">{questions.length} questions across {Object.keys(DAY_LABELS).length} days</p>
              </div>
            </div>
            <button
              onClick={() => setModal({ mode: "create" })}
              className="flex items-center gap-2 bg-white text-indigo-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-indigo-50 transition shadow"
            >
              <FontAwesomeIcon icon={faPlus} />
              New Question
            </button>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: "Total Questions", value: questions.length, icon: faListOl },
              { label: "Total Respondents", value: totalRespondents.toLocaleString(), icon: faUsers },
              { label: "Questions Answered", value: totalAnswered, icon: faChartBar },
              { label: "Coverage", value: `${completionRate}%`, icon: faFire },
            ].map(s => (
              <div key={s.label} className="bg-white/15 rounded-xl px-4 py-3 backdrop-blur-sm border border-white/20">
                <p className="text-indigo-200 text-xs">{s.label}</p>
                <p className="text-white text-xl font-bold mt-0.5">{s.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6 space-y-5">
        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3">
          <select className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white shadow-sm"
            value={filterDay} onChange={(e) => setFilterDay(e.target.value)}>
            <option value="">All Days</option>
            {Object.entries(DAY_LABELS).map(([d, label]) => (
              <option key={d} value={d}>{label}</option>
            ))}
          </select>
          <select className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white shadow-sm"
            value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button
            onClick={() => setShowAnalytics(a => !a)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition ${showAnalytics ? "bg-indigo-50 border-indigo-200 text-indigo-700 font-medium" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}
          >
            <FontAwesomeIcon icon={faChartBar} />
            {showAnalytics ? "Hide Analytics" : "Show Analytics"}
          </button>
          <button onClick={fetchData} className="ml-auto text-xs text-gray-400 hover:text-gray-600 underline">
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <FontAwesomeIcon icon={faSpinner} spin className="text-3xl text-gray-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <FontAwesomeIcon icon={faBrain} className="text-4xl mb-3" />
            <p>No questions match your filters.</p>
          </div>
        ) : (
          Object.entries(DAY_LABELS).map(([day, dayLabel]) => {
            const qs = byDay[Number(day)] || [];
            if (filterDay !== "" && String(day) !== filterDay) return null;
            if (qs.length === 0 && filterDay === "") return null;
            return (
              <div key={day}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-px flex-1 bg-gray-200" />
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-2">
                    {dayLabel} · {qs.length} question{qs.length !== 1 ? "s" : ""}
                  </span>
                  <div className="h-px flex-1 bg-gray-200" />
                </div>
                <div className="space-y-3">
                  {qs.map((q) => (
                    <QuestionCard
                      key={q._id}
                      q={q}
                      showAnalytics={showAnalytics}
                      onEdit={() => setModal({ mode: "edit", data: q })}
                      onDelete={() => handleDelete(q._id)}
                      deleting={deleting === q._id}
                    />
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      {modal && (
        <QuestionModal
          initial={modal.data}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};

export default AlignmentQuestions;
